import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { LanguageSelector } from '../components/LanguageSelector';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Loader2, Mail, CheckCircle, Sun, Moon } from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const LOGO_URL = "/logo-alphabit.png";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { t } = useLanguage();
  const { isDark, toggleTheme } = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
      toast.success(t('auth.emailSent'));
    } catch (error) {
      // Always show success to prevent email enumeration
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 sm:p-8 ${
        isDark ? 'bg-[#0B0F2A]' : 'bg-gradient-to-br from-slate-50 to-slate-100'
      }`}>
        <Card className={`max-w-md w-full ${
          isDark 
            ? 'bg-[#0F0F2F] border-purple-500/30' 
            : 'bg-white border-slate-200'
        }`}>
          <CardContent className="p-6 sm:p-8 text-center">
            <div className={`w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center ${
              isDark ? 'bg-emerald-500/20' : 'bg-emerald-100'
            }`}>
              <CheckCircle className={`w-8 h-8 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
            </div>
            <h1 className={`font-heading text-xl sm:text-2xl font-bold mb-4 ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}>
              {t('auth.emailSent')}
            </h1>
            <p className={`text-sm sm:text-base mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              {t('auth.emailSentMessage')}
            </p>
            <Link to="/login">
              <Button className={`w-full ${
                isDark 
                  ? 'bg-gradient-to-r from-[#6A00FF] to-[#9D4DFF] hover:from-[#7B1FFF] hover:to-[#AE6EFF]' 
                  : 'bg-primary hover:bg-primary/90'
              }`}>
                {t('auth.backToLogin')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 sm:p-8 ${
      isDark ? 'bg-[#0B0F2A]' : 'bg-gradient-to-br from-slate-50 to-slate-100'
    }`}>
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <Link to="/login" className={`inline-flex items-center gap-2 transition-colors text-sm sm:text-base ${
            isDark ? 'text-gray-400 hover:text-[#9D4DFF]' : 'text-slate-600 hover:text-primary'
          }`}>
            <ArrowLeft className="w-4 h-4" />
            {t('auth.backToLogin')}
          </Link>
          <div className="flex items-center gap-2">
            <button 
              onClick={toggleTheme}
              className={`w-8 h-8 flex items-center justify-center rounded-full transition-all hover:scale-110 ${
                isDark 
                  ? 'bg-purple-500/20 text-yellow-400 hover:bg-purple-500/30' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid="theme-toggle"
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <LanguageSelector />
          </div>
        </div>
        
        <div className="flex items-center gap-3 mb-6 sm:mb-8">
          <img src={LOGO_URL} alt="AlphaBit" className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl" />
          <span className={`font-heading font-bold text-xl sm:text-2xl ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Alpha<span className={isDark ? 'text-[#9D4DFF]' : 'text-violet-600'}>Bit</span>
          </span>
        </div>

        <Card className={`${
          isDark 
            ? 'bg-[#0F0F2F] border-purple-500/30 shadow-purple-500/10' 
            : 'bg-white border-slate-200'
        }`}>
          <CardContent className="p-6 sm:p-8">
            <div className={`w-12 h-12 sm:w-14 sm:h-14 mb-4 sm:mb-6 rounded-xl flex items-center justify-center ${
              isDark ? 'bg-purple-500/20' : 'bg-primary/10'
            }`}>
              <Mail className={`w-6 h-6 sm:w-7 sm:h-7 ${isDark ? 'text-[#9D4DFF]' : 'text-primary'}`} />
            </div>
            
            <h1 className={`font-heading text-xl sm:text-2xl font-bold mb-2 ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}>
              {t('auth.forgotPasswordTitle')}
            </h1>
            <p className={`text-sm sm:text-base mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              {t('auth.forgotPasswordSubtitle')}
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className={isDark ? 'text-gray-300' : ''}>{t('common.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="forgot-email"
                  className={`h-11 rounded-xl ${
                    isDark 
                      ? 'bg-white/5 border-purple-500/30 text-white placeholder:text-gray-500 focus:border-[#9D4DFF] focus:ring-[#9D4DFF]/20' 
                      : ''
                  }`}
                />
              </div>
              <Button 
                type="submit" 
                className={`w-full h-11 rounded-xl ${
                  isDark 
                    ? 'bg-gradient-to-r from-[#6A00FF] to-[#9D4DFF] hover:from-[#7B1FFF] hover:to-[#AE6EFF]' 
                    : 'bg-primary hover:bg-primary/90'
                }`}
                disabled={loading}
                data-testid="forgot-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('auth.sending')}
                  </>
                ) : (
                  t('auth.sendResetLink')
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
